import React, { useState, useEffect } from "react";
import { FaCheck, FaMinus, FaTimes } from "react-icons/fa"; // Added FaTimes for cross icon
import Cookies from "js-cookie";
import PopupMessage from "../layouts/PopupMessage";
import config from "../../config";
import styles from "../css/css_pages/ChecklistPopup.module.css";

const ChecklistPopupTSO = ({ checklist, onClose }) => {
  const [checklistItems, setChecklistItems] = useState([]);
  const [clientNote, setClientNote] = useState("");
  const [alertMessage, setAlertMessage] = useState(null);
  const [rating, setRating] = useState(0);

  useEffect(() => {
    if (checklist && checklist.schedule_id) {
      fetchChecklist(checklist.schedule_id);
    }
  }, [checklist]);

  const showPopupMessage = (message) => {
    setAlertMessage(message);
  };

  const closePopupMessage = () => {
    setAlertMessage(null);
  };

  const handleStarClick = (selectedRating) => {
    setRating(selectedRating);
  };

  const fetchChecklist = async (scheduleId) => {
    const token = Cookies.get("authToken");
    try {
      const response = await fetch(
        `${config.API_URL}/client/get-checklist?schedule_id=${scheduleId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch checklist");
      }

      const data = await response.json();
      console.log("Fetched checklist data:", data);

      let tsoTicked = {};
      let clientTicked = {};

      try {
        tsoTicked =
          data[0].tso_ticked && typeof data[0].tso_ticked === "string"
            ? JSON.parse(data[0].tso_ticked.replace(/'/g, '"'))
            : data[0].tso_ticked || {};

        clientTicked =
          data[0].client_ticked && typeof data[0].client_ticked === "string"
            ? JSON.parse(data[0].client_ticked.replace(/'/g, '"'))
            : data[0].client_ticked || {};
      } catch (parseError) {
        console.error("Error parsing tso_ticked or client_ticked:", parseError);
        return;
      }

      const mappedChecklistItems = data[0].checklist.map((item) => ({
        id: item.number,
        description: item.description,
        tso:
          tsoTicked[item.number] === 1
            ? "Done"
            : tsoTicked[item.number] === 0
            ? "Could Not Done"
            : "Pending",
        client:
          clientTicked[item.number] === 1
            ? "Done"
            : clientTicked[item.number] === 0
            ? "Could Not Done"
            : "Pending",
      }));

      setChecklistItems(mappedChecklistItems);
    } catch (error) {
      console.error("Error fetching checklist:", error);
      showPopupMessage("Failed to fetch checklist");
    }
  };

  const updateChecklistStatus = async () => {
    const token = Cookies.get("authToken");

    if (!clientNote) {
      showPopupMessage("Please provide a note.");
      return;
    }

    try {
      const response = await fetch(
        `${config.API_URL}/tso-online/update-checklist-status`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            schedule_id: checklist.schedule_id,
            tso_online_note: clientNote,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update checklist");
      }

      showPopupMessage("Checklist updated successfully.");
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Error updating checklist:", error);
      showPopupMessage("Failed to update checklist");
    }
  };

  // Helper function to render the appropriate icon based on status
  const renderStatusIcon = (status) => {
    switch (status) {
      case "Done":
        return <FaCheck className={styles.iconDone} />;
      case "Could Not Done":
        return <FaTimes className={styles.iconCross} />; // Using FaTimes for cross icon
      case "Pending":
      default:
        return <FaMinus className={styles.iconPending} />;
    }
  };

  return (
    <>
      {alertMessage && (
        <PopupMessage message={alertMessage} onClose={closePopupMessage} />
      )}
      <div className={styles.popupOverlay}>
        <div className={styles.popupContent}>
          <div className={styles.popupHeader}>
            <h2>Checklist for Schedule No: {checklist?.schedule_no}</h2>
            <button className={styles.closeButton} onClick={onClose}>
              &times;
            </button>
          </div>
          <div className={styles.checklistContainer}>
            <div className={styles.checklistTableContainer}>
              <table className={styles.checklistTable}>
                <thead>
                  <tr>
                    <th className={styles.idColumn}>ID</th>
                    <th className={styles.descriptionColumn}>Description</th>
                    <th className={styles.statusColumn}>TSO</th>
                    <th className={styles.statusColumn}>Client</th>
                  </tr>
                </thead>
                <tbody>
                  {checklistItems.map((item) => (
                    <tr key={item.id}>
                      <td className={styles.idColumn}>{item.id}</td>
                      <td className={styles.descriptionColumn}>{item.description}</td>
                      <td className={`${styles.statusCell} ${styles.disabledCell}`} title="TSO checklist cannot be modified by the client">
                        {renderStatusIcon(item.tso)}
                      </td>
                      <td className={styles.statusCell}>
                        <button className={`${styles.statusButton} ${styles.disabledButton}`}>
                          {renderStatusIcon(item.client)}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className={styles.formSection}>
              <div className={styles.formGroup}>
                <label htmlFor="clientNote">Note</label>
                <textarea
                  id="clientNote"
                  rows="2"
                  placeholder="Enter TSO Online note here..."
                  value={clientNote}
                  onChange={(e) => setClientNote(e.target.value)}
                  className={styles.clientNote}
                />
              </div>
            </div>
            <div className={styles.actionButtons}>
              {checklist.schedule_status === "completed" ||
              checklist.schedule_status === "closed" ? (
                <button
                  className={styles.submitButton}
                  onClick={updateChecklistStatus}
                >
                  Save & Confirm
                </button>
              ) : (
                <span
                  style={{ color: "gray", cursor: "not-allowed" }}
                  title="Not available"
                >
                  <button
                    className={styles.submitButton}
                    style={{ backgroundColor: "lightgray", cursor: "not-allowed" }}
                  >
                    Save & Confirm
                  </button>
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChecklistPopupTSO;